import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Clock, TrendUp, Lightbulb, House, Users, Buildings, Handshake,
  ClipboardText, Folder, ShoppingCart, Lightning, Shield, Database,
  UserCircle, LinkSimple, Megaphone, Globe, FileText, ChartLineUp,
  Gear, TextT, ClockCounterClockwise, CalendarBlank, Scales,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

// All apps registered in the system — single source of truth
const APP_CATALOG = [
  { id: 'dashboard',       path: '/dashboard',       label: 'Dashboard',       subtitle: 'Overview',              icon: House,             color: 'blue' },
  { id: 'contacts',        path: '/contacts',        label: 'Contacts',        subtitle: 'CRM records',           icon: Users,             color: 'orange' },
  { id: 'companies',       path: '/companies',       label: 'Companies',       subtitle: 'Business entities',     icon: Buildings,         color: 'teal' },
  { id: 'deals',           path: '/deals',           label: 'Deals',           subtitle: 'Pipeline',              icon: Handshake,         color: 'blue' },
  { id: 'tasks',           path: '/tasks',           label: 'Tasks',           subtitle: 'Assignments',           icon: ClipboardText,     color: 'green' },
  { id: 'documents',       path: '/documents',       label: 'Documents',       subtitle: 'Data rooms',            icon: Folder,            color: 'teal' },
  { id: 'procurement',     path: '/procurement',     label: 'Procurement',     subtitle: 'Sourcing',              icon: ShoppingCart,      color: 'grey' },
  { id: 'workflows',       path: '/workflows',       label: 'Workflows',       subtitle: 'Automation',            icon: Lightning,         color: 'amber' },
  { id: 'intelligence',    path: '/intelligence',    label: 'Intelligence',    subtitle: 'BI & analytics',        icon: ChartLineUp,       color: 'purple' },
  { id: 'marketplace',     path: '/marketplace',     label: 'Marketplace',     subtitle: 'Listings',              icon: Globe,             color: 'indigo' },
  { id: 'deal-prep',       path: '/deal-prep',       label: 'Deal Prep Lab',   subtitle: 'Readiness',             icon: ClipboardText,     color: 'amber' },
  { id: 'data-management', path: '/data-management', label: 'Data Management', subtitle: 'Access & audit',        icon: Database,          color: 'red' },
  { id: 'client-portal',   path: '/client-portal',   label: 'Client Portal',   subtitle: 'External B2B',          icon: UserCircle,        color: 'indigo' },
  { id: 'share-links',     path: '/share-links',     label: 'Share Links',     subtitle: 'Monitor & revoke',      icon: LinkSimple,        color: 'amber' },
  { id: 'affiliate',       path: '/affiliate',       label: 'Affiliate',       subtitle: 'Referrals',             icon: Megaphone,         color: 'green' },
  { id: 'reports',         path: '/reports',         label: 'Reports',         subtitle: 'Analytics',             icon: ChartLineUp,       color: 'teal' },
  { id: 'calendar',        path: '/calendar',        label: 'Calendar',        subtitle: 'Events',                icon: CalendarBlank,     color: 'orange' },
  { id: 'custom-fields',   path: '/custom-fields',   label: 'Custom Fields',   subtitle: 'Configuration',         icon: TextT,             color: 'purple' },
  { id: 'audit-trail',     path: '/audit-trail',     label: 'Audit Trail',     subtitle: 'Compliance log',        icon: ClockCounterClockwise, color: 'red' },
  { id: 'webhooks',        path: '/webhooks',        label: 'Webhooks',        subtitle: 'Integrations',          icon: Globe,             color: 'teal' },
  { id: 'access-control',  path: '/access-control',  label: 'Access Control',  subtitle: 'RBAC',                  icon: Shield,            color: 'red' },
  { id: 'ai-assistant',    path: '/ai-assistant',    label: 'AI Assistant',    subtitle: 'GPT chat',              icon: Lightbulb,         color: 'purple' },
  { id: 'projects',        path: '/projects',        label: 'Projects',        subtitle: 'Plan & deliver',        icon: ClipboardText,     color: 'purple' },
  { id: 'supply-chain',    path: '/supply-chain',    label: 'Supply Chain',    subtitle: 'Suppliers & inventory', icon: ShoppingCart,      color: 'orange' },
];

const BY_ID = Object.fromEntries(APP_CATALOG.map(a => [a.id, a]));

// Recommendations by role — used as fallback when usage history is empty
const ROLE_RECOMMENDATIONS = {
  admin: ['data-management', 'access-control', 'audit-trail', 'intelligence', 'share-links', 'client-portal'],
  manager: ['deals', 'deal-prep', 'documents', 'reports', 'tasks', 'intelligence'],
  user: ['tasks', 'contacts', 'calendar', 'documents', 'deals', 'ai-assistant'],
  default: ['dashboard', 'deals', 'contacts', 'documents', 'tasks', 'calendar'],
};

const LS_KEYS = {
  favorites: (uid) => `apps_favorites:${uid}`,
  usage:     (uid) => `apps_usage:${uid}`,
  recent:    (uid) => `apps_recent:${uid}`,
};

function loadJSON(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}

export default function AppsOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id || 'anonymous';
  const [tab, setTab] = useState(() => localStorage.getItem('apps_tab') || 'favorites');
  const [favorites, setFavorites] = useState(() => loadJSON(LS_KEYS.favorites(uid), []));
  const [usage, setUsage] = useState(() => loadJSON(LS_KEYS.usage(uid), {}));
  const [recent, setRecent] = useState(() => loadJSON(LS_KEYS.recent(uid), []));

  useEffect(() => { localStorage.setItem('apps_tab', tab); }, [tab]);

  const toggleFavorite = useCallback((appId) => {
    setFavorites((prev) => {
      const next = prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId];
      localStorage.setItem(LS_KEYS.favorites(uid), JSON.stringify(next));
      return next;
    });
  }, [uid]);

  const launchApp = useCallback((appId) => {
    // Track usage + recent
    const newUsage = { ...usage, [appId]: (usage[appId] || 0) + 1 };
    const newRecent = [appId, ...recent.filter((id) => id !== appId)].slice(0, 20);
    setUsage(newUsage);
    setRecent(newRecent);
    localStorage.setItem(LS_KEYS.usage(uid), JSON.stringify(newUsage));
    localStorage.setItem(LS_KEYS.recent(uid), JSON.stringify(newRecent));
    navigate(BY_ID[appId].path);
  }, [navigate, usage, recent, uid]);

  // Compute the list of apps for the active tab
  const getAppsForTab = () => {
    switch (tab) {
      case 'favorites':
        return favorites.map(id => BY_ID[id]).filter(Boolean);
      case 'most-used':
        return Object.entries(usage)
          .sort((a, b) => b[1] - a[1])
          .map(([id]) => BY_ID[id])
          .filter(Boolean)
          .slice(0, 12);
      case 'recent':
        return recent.map(id => BY_ID[id]).filter(Boolean).slice(0, 12);
      case 'recommended': {
        const role = user?.role || 'default';
        const ids = ROLE_RECOMMENDATIONS[role] || ROLE_RECOMMENDATIONS.default;
        return ids.map(id => BY_ID[id]).filter(Boolean);
      }
      default:
        return [];
    }
  };

  const apps = getAppsForTab();

  const emptyMessages = {
    favorites: { icon: Star, title: 'No favorites yet', hint: 'Click the star on any app to add it here' },
    'most-used': { icon: TrendUp, title: 'No usage data yet', hint: 'Your most-opened apps will show up here' },
    recent: { icon: Clock, title: 'No recent activity', hint: 'Apps you open will appear here' },
    recommended: { icon: Lightbulb, title: 'No recommendations', hint: 'Recommendations based on your role' },
  };

  return (
    <div data-testid="apps-overview">
      <div className="flex items-center gap-1 mb-4">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mr-4">Apps</p>
      </div>

      {/* Tab strip — SAP-style underlined tabs */}
      <div className="border-b border-border mb-4">
        <div className="flex gap-6">
          <TabButton active={tab === 'favorites'}   onClick={() => setTab('favorites')}   icon={Star}      label="Favorites"     count={favorites.length} testId="tab-favorites" />
          <TabButton active={tab === 'most-used'}   onClick={() => setTab('most-used')}   icon={TrendUp}   label="Most Used"     count={Object.keys(usage).length} testId="tab-most-used" />
          <TabButton active={tab === 'recent'}      onClick={() => setTab('recent')}      icon={Clock}     label="Recently Used" count={recent.length} testId="tab-recent" />
          <TabButton active={tab === 'recommended'} onClick={() => setTab('recommended')} icon={Lightbulb} label="Recommended"   testId="tab-recommended" />
        </div>
      </div>

      {/* App grid */}
      {apps.length === 0 ? (
        <EmptyState {...emptyMessages[tab]} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {apps.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              isFavorite={favorites.includes(app.id)}
              onLaunch={() => launchApp(app.id)}
              onToggleFav={() => toggleFavorite(app.id)}
              usageCount={usage[app.id]}
              showUsage={tab === 'most-used'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative flex items-center gap-2 py-3 text-sm font-medium transition-colors ${
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon size={16} weight={active ? 'fill' : 'regular'} />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className={`inline-flex items-center justify-center text-xs rounded-full min-w-5 h-5 px-1.5 ${
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}>
          {count}
        </span>
      )}
      {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
    </button>
  );
}

// Pantone-palette mapping for app tiles (mirrors the Fiori tiles in index.css)
const APP_COLORS = {
  blue:    { bg: 'bg-[#002677]/10',  text: 'text-[#002677]' },   // Reflex Blue
  red:     { bg: 'bg-[#E4002B]/10',  text: 'text-[#E4002B]' },   // Red 186
  orange:  { bg: 'bg-[#FF671F]/15',  text: 'text-[#C94E10]' },   // Orange 021
  teal:    { bg: 'bg-[#00A693]/15',  text: 'text-[#007A6C]' },   // Teal 3275
  green:   { bg: 'bg-[#00B140]/15',  text: 'text-[#00813A]' },   // Green 354
  purple:  { bg: 'bg-[#8347AD]/15',  text: 'text-[#5B2E91]' },   // Purple 2582
  indigo:  { bg: 'bg-[#41B6E6]/20',  text: 'text-[#1E7CA6]' },   // Sky 298
  amber:   { bg: 'bg-[#FFD100]/25',  text: 'text-[#B5940A]' },   // Yellow 012
  pink:    { bg: 'bg-[#E10098]/10',  text: 'text-[#B50079]' },   // Rubine / Magenta
  lime:    { bg: 'bg-[#78BE20]/20',  text: 'text-[#4E8016]' },   // Lime 368
  slate:   { bg: 'bg-[#5B6D99]/15',  text: 'text-[#3D4A5C]' },   // Slate 2718
  rose:    { bg: 'bg-[#EA638F]/15',  text: 'text-[#B84268]' },   // Rose 191
  grey:    { bg: 'bg-[#6B7280]/15',  text: 'text-[#4B5563]' },   // Pantone Cool Grey 9
};

function AppCard({ app, isFavorite, onLaunch, onToggleFav, usageCount, showUsage }) {
  const Icon = app.icon;
  const c = APP_COLORS[app.color] || APP_COLORS.blue;
  return (
    <div className="relative group">
      <button
        onClick={onLaunch}
        data-testid={`app-${app.id}`}
        className="w-full bg-card border border-border hover:border-primary rounded-sm p-3 flex items-start gap-3 text-left transition-all hover:shadow-md"
      >
        <div className={`h-10 w-10 rounded-sm ${c.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} weight="duotone" className={c.text} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{app.label}</p>
          <p className="text-xs text-muted-foreground truncate">{app.subtitle}</p>
          {showUsage && usageCount && (
            <p className="text-[10px] text-muted-foreground mt-0.5">Opened {usageCount}x</p>
          )}
        </div>
      </button>
      {/* Favorite star overlay */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
        data-testid={`fav-${app.id}`}
        className={`absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center transition-all ${
          isFavorite
            ? 'text-amber-500 opacity-100 hover:bg-amber-500/10'
            : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-muted'
        }`}
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Star size={14} weight={isFavorite ? 'fill' : 'regular'} />
      </button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="text-center py-10 border border-dashed border-border rounded-sm bg-muted/20">
      <Icon size={36} className="mx-auto text-muted-foreground opacity-40 mb-2" />
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}
